import { Injectable } from '@angular/core';
import { StateManagerProjectService } from '@state-manager/state-manager-project.service';
import cornerstoneTools from 'cornerstone-tools/dist/cornerstoneTools.js';
const segModule = cornerstoneTools.getModule('segmentation');
const { getters, setters } = segModule;
// custom code
import { getLabelmap2D, addLabelmap3D, addLabelmap2D } from './getLabelmap2D';
import { setLabelmap3DForElement } from './setLabelMap3DCustom';
import { BASE_COLOR_LUT, BASE_COLOR_LUT_TIBIA, SEGMENT_INDEX, CASE_TYPE, CASE_IMAGING } from '@viewer/cornerstone.data';
// override function
getters.labelmap2D = getLabelmap2D;
setters.addLabelmap3D = addLabelmap3D;
setters.addLabelmap2D = addLabelmap2D;
setters.labelmap3DForElement = setLabelmap3DForElement;

@Injectable()
export class SegmentationService {
  constructor(
    private _stateManagerProjectService: StateManagerProjectService
  ) {}

  /**
   * Add segmentation mask to segmentation module
   * @param {Object} maskProps - The mask properties (labelId, color and opacity)
   * @param {TypedArray} - The mask data array
   * @param {String} elementId - The target html element Id or its DOM HTMLElement
   * @returns {Promise} - Return a promise which will resolve when segmentation mask is added
   */
  public addSegmentationMask(
    props: any,
    data: any,
    elementId: string
  ): Promise<unknown> {
    const promise = new Promise(async (resolve) => {
      const element = this._isElement(elementId)
        ? elementId
        : document.getElementById(elementId);
      if (!element) {
        console.error('invalid html element: ' + elementId);
        return;
      }
      
      const res = await setters.labelmap3DForElement(
        element,
        data.buffer,
        props.labelId,
        [],
        undefined,
        1,
        this._stateManagerProjectService.maskNrrdType
      );
      // if user set a color property, use that color for all segments on the labelmap
      const ACTIVE_SEGMENT_INDEX = this.fetchSegmentIndex();
      setters.activeSegmentIndex(element, ACTIVE_SEGMENT_INDEX);
      const lut = props.color
        ? this.generateUniformLUT(props.color, props.opacity)
        : this._stateManagerProjectService.caseType === CASE_TYPE.FEMUR ? BASE_COLOR_LUT : BASE_COLOR_LUT_TIBIA;
      setters.colorLUT(props.labelId, lut);
      // bind labelmap to colorLUT
      const labelmap3d = getters.labelmap3D(element, props.labelId);
      setters.colorLUTIndexForLabelmap3D(labelmap3d, props.labelId);

      resolve(null);
    });
    return promise;
  }

  /**
   * Generate the custom LUT - multiple volume version
   * @param {String} hex_color - color for LUT in the #RRGGBB form
   * @param {NUmber} opacity - number between 0 and 1
   */
  public generateUniformLUT(hex_color: string, opacity: number) {
    const lut = new Array(segModule.configuration.segmentsPerLabelmap);
    const rgb_color = this._hexToRgb(hex_color);
    const rgba_color = rgb_color?.concat(Math.round(opacity * 255));
    lut.fill(rgba_color);
    return lut;
  }

  private _hexToRgb(hex) {
    const regEx = new RegExp(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    var result = regEx.exec(hex);    
    return result
      ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16),
        ]
      : null;
  }

  /**
   * Check if a div tag is a valid DOM HTMLElement
   * @instance
   * @function isElement
   * @param {Object} o - The div tag
   * @return {Boolean} - True if is an element otherwise returns False
   */
  private _isElement(o: HTMLElement | string) {
    return typeof HTMLElement === 'object'
      ? o instanceof HTMLElement //DOM2
      : o &&
          typeof o === 'object' &&
          o !== null &&
          o.nodeType === 1 &&
          typeof o.nodeName === 'string';
  }

  fetchSegmentIndex(): number {
    const SEG_INDEX_ITEM: number = 0;
    const nrrdSegmentIndex: number[] = [];

    for (const index of this._stateManagerProjectService.basePixelData!) {
      if (
        index !== 0 &&
        nrrdSegmentIndex.findIndex(
          (usedSegment: number) => usedSegment === index
        ) === -1
      ) {
        nrrdSegmentIndex.push(index);
      }
    }

    return nrrdSegmentIndex[SEG_INDEX_ITEM];
  }
}
